/**
 * Acompanhamento de um envio: uma linha por arquivo, recolhida.
 *
 * O que a tela precisa responder, nesta ordem: *acabou?*, *algum deu
 * problema?*, e só então *o que aconteceu com este aqui*. Por isso a linha
 * fechada mostra só situação e números, e a expansão traz as conferências —
 * oito arquivos com quatro conferências cada seriam 32 linhas de detalhe
 * empilhadas, e ninguém acha o problema nisso.
 *
 * A linha que **não** fechou já nasce aberta: é a que o usuário veio ver.
 */
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { mensagemErro } from '../api/axios';
import { estadoLote, retomarLote } from '../api/fluxfin';
import {
  Alerta,
  Badge,
  Botao,
  LoadingSpinner,
  PageHeader,
  Painel,
  Tabela,
  Th,
} from '../components/common';
import { brl, numero } from '../utils/formato';

/** Como cada situação de item aparece. */
const SITUACAO = {
  na_fila: { rotulo: 'Na fila', tom: 'arquivado', icone: 'bi-hourglass' },
  extraindo: { rotulo: 'Lendo', tom: 'pendente', icone: 'bi-arrow-repeat' },
  conciliando: { rotulo: 'Consultando o Bioslab', tom: 'pendente', icone: 'bi-arrow-repeat' },
  concluido: { rotulo: 'Pronto', tom: 'confirmado', icone: 'bi-check-circle-fill' },
  reprovado: { rotulo: 'Não fechou', tom: 'reprovado', icone: 'bi-x-octagon-fill' },
  erro: { rotulo: 'Não entrou', tom: 'reprovado', icone: 'bi-exclamation-octagon-fill' },
};

const EM_ANDAMENTO = ['na_fila', 'extraindo', 'conciliando'];

function Conferencias({ validacoes }) {
  if (!validacoes || validacoes.length === 0) {
    return (
      <p style={{ fontSize: 13, color: 'var(--ff-muted)', margin: 0 }}>
        Nenhuma conferência registrada — o arquivo não chegou a ser lido.
      </p>
    );
  }
  return (
    <ul className="list-unstyled m-0">
      {validacoes.map((v) => (
        <li key={v.regra} className="d-flex align-items-start gap-2 mb-2">
          <i
            className={`bi ${v.passou ? 'bi-check-circle-fill' : 'bi-x-circle-fill'}`}
            style={{
              color: v.passou ? '#2fa36c' : '#d92d20',
              fontSize: 15,
              marginTop: 2,
              flexShrink: 0,
            }}
            aria-hidden="true"
          />
          <div>
            <div style={{ fontSize: 13, fontWeight: 500 }}>{v.descricao}</div>
            {v.detalhe && (
              <div className="ff-mono" style={{ fontSize: 12, color: 'var(--ff-muted)' }}>
                {v.detalhe}
              </div>
            )}
            {!v.passou && v.esperado != null && (
              <div className="ff-mono" style={{ fontSize: 12, color: '#9a5b06' }}>
                esperado {brl(v.esperado)} · obtido {brl(v.obtido)}
              </div>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}

export default function LoteProgresso() {
  const { id } = useParams();
  const [lote, setLote] = useState(null);
  const [abertos, setAbertos] = useState({});
  const [erro, setErro] = useState('');
  const [carregando, setCarregando] = useState(true);
  // Só abre sozinho uma vez: reabrir a cada consulta ignoraria o usuário que
  // acabou de fechar a linha.
  const jaAbriuProblemas = useRef(false);

  const buscar = useCallback(() => {
    estadoLote(id)
      .then((r) => {
        setLote(r.data);
        if (!jaAbriuProblemas.current && !r.data.em_andamento) {
          jaAbriuProblemas.current = true;
          const problemas = {};
          r.data.itens
            .filter((i) => i.status === 'reprovado' || i.status === 'erro')
            .forEach((i) => {
              problemas[i.id_item] = true;
            });
          if (Object.keys(problemas).length) setAbertos((a) => ({ ...a, ...problemas }));
        }
      })
      .catch((e) => setErro(mensagemErro(e, 'Não foi possível carregar o envio.')))
      .finally(() => setCarregando(false));
  }, [id]);

  useEffect(() => {
    buscar();
  }, [buscar]);

  // Consulta a cada 2 s enquanto há fila; para assim que termina, para não
  // ficar batendo no servidor por nada.
  useEffect(() => {
    if (!lote?.em_andamento) return undefined;
    const t = setInterval(buscar, 2000);
    return () => clearInterval(t);
  }, [lote?.em_andamento, buscar]);

  if (carregando) return <LoadingSpinner />;
  if (!lote) {
    return (
      <Alerta tipo="erro" onFechar={() => setErro('')}>
        {erro || 'Envio não encontrado.'}
      </Alerta>
    );
  }

  const prontos = lote.itens.filter((i) => i.status === 'concluido').length;
  const problemas = lote.itens.filter(
    (i) => i.status === 'reprovado' || i.status === 'erro'
  ).length;
  const parados = lote.itens.filter((i) => EM_ANDAMENTO.includes(i.status)).length;
  const pct = Math.round((lote.concluidos / Math.max(lote.qtd_arquivos, 1)) * 100);

  const retomar = async () => {
    setErro('');
    try {
      await retomarLote(id);
      buscar();
    } catch (e) {
      setErro(mensagemErro(e, 'Não foi possível retomar o envio.'));
    }
  };

  return (
    <>
      <PageHeader
        titulo="Envio de recebimentos"
        subtitulo={`${lote.operadora || '—'} · ${numero(lote.qtd_arquivos)} arquivo${lote.qtd_arquivos === 1 ? '' : 's'}`}
        acoes={
          <Link to="/arquivos" className="ff-btn ff-btn--secundario">
            Ver todos os arquivos
          </Link>
        }
      />

      <Alerta tipo="erro" onFechar={() => setErro('')}>
        {erro}
      </Alerta>

      <Painel>
        <div className="p-4">
          <div className="d-flex flex-wrap align-items-center gap-3 mb-2">
            <div style={{ flex: 1, minWidth: 220 }}>
              <div className="progress" style={{ height: 8, borderRadius: 4 }}>
                <div
                  className="progress-bar"
                  style={{
                    width: `${pct}%`,
                    background: lote.em_andamento ? 'var(--ff-blue)' : 'var(--ff-green)',
                    transition: 'width .4s',
                  }}
                />
              </div>
            </div>
            <div className="ff-mono" style={{ fontWeight: 600 }}>
              {lote.concluidos} / {lote.qtd_arquivos}
            </div>
            {lote.em_andamento ? (
              <Badge tom="pendente">Em andamento</Badge>
            ) : problemas > 0 ? (
              <Badge tom="reprovado">
                {problemas} com problema
              </Badge>
            ) : (
              <Badge tom="confirmado">Tudo pronto</Badge>
            )}
          </div>

          <p style={{ fontSize: 13, color: 'var(--ff-muted)', margin: 0 }}>
            {lote.etapa || '—'}
            {lote.sincronizar_bioslab && lote.em_andamento && (
              <>
                {' '}
                · A consulta ao Bioslab roda um arquivo por vez, em lotes — é a produção
                do cliente.
              </>
            )}
          </p>

          {!lote.em_andamento && parados > 0 && (
            <div className="ff-aviso ff-aviso--atencao mt-3">
              <i className="bi bi-exclamation-triangle" aria-hidden="true" />
              <div>
                {parados} arquivo(s) ficaram pelo caminho — normalmente porque o servidor
                reiniciou no meio do envio. O conteúdo continua guardado.
                <div className="mt-2">
                  <Botao variante="secundario" pequeno onClick={retomar}>
                    Retomar de onde parou
                  </Botao>
                </div>
              </div>
            </div>
          )}

          {!lote.em_andamento && problemas > 0 && (
            <div className="ff-aviso ff-aviso--atencao mt-3">
              <i className="bi bi-info-circle" aria-hidden="true" />
              <div>
                {prontos} de {lote.qtd_arquivos} arquivo(s) entraram normalmente. Os
                demais estão abertos abaixo, com o motivo. Um arquivo que não fecha{' '}
                <strong>não impede os outros</strong> — e os dados dele não entram na
                conciliação.
              </div>
            </div>
          )}
        </div>
      </Painel>

      <div className="mt-3">
        <Painel
          titulo="Arquivos deste envio"
          subtitulo="Cada um é conferido sozinho. Clique para ver as conferências."
        >
          <Tabela>
            <thead>
              <tr>
                <Th>Arquivo</Th>
                <Th>Competência</Th>
                <Th>Situação</Th>
                <Th alinhar="right">Guias</Th>
                <Th alinhar="right">Total extraído</Th>
                <Th alinhar="right">No documento</Th>
                <Th alinhar="right">Bioslab</Th>
              </tr>
            </thead>
            <tbody>
              {lote.itens.map((i) => {
                const s = SITUACAO[i.status] || {
                  rotulo: i.status,
                  tom: 'arquivado',
                  icone: 'bi-question-circle',
                };
                const aberto = !!abertos[i.id_item];
                const girando = EM_ANDAMENTO.includes(i.status) && i.status !== 'na_fila';
                return (
                  <Fragment key={i.id_item}>
                    <tr
                      className="ff-linha-clicavel"
                      onClick={() =>
                        setAbertos((a) => ({ ...a, [i.id_item]: !a[i.id_item] }))
                      }
                    >
                      <td className="col-codigo">
                        <i
                          className={`bi bi-chevron-${aberto ? 'down' : 'right'} me-2`}
                          aria-hidden="true"
                        />
                        {i.nome_original}
                      </td>
                      <td className="col-meta">{i.competencia || '—'}</td>
                      <td>
                        <Badge tom={s.tom}>
                          <i
                            className={`bi ${s.icone} me-1`}
                            aria-hidden="true"
                            // O ícone de recarga gira enquanto o item está em
                            // trabalho: sem isso, uma fila lenta parece travada.
                            style={girando ? { animation: 'ff-girar 1.2s linear infinite', display: 'inline-block' } : undefined}
                          />
                          {s.rotulo}
                        </Badge>
                      </td>
                      <td className="col-qtd">
                        {i.qtd_guias == null ? '—' : numero(i.qtd_guias)}
                      </td>
                      <td className="col-valor">
                        {i.total_extraido == null ? '—' : brl(i.total_extraido)}
                      </td>
                      <td className="col-qtd" style={{ color: 'var(--ff-muted)' }}>
                        {i.total_pago_impresso == null ? '—' : brl(i.total_pago_impresso)}
                      </td>
                      <td className="col-qtd">
                        {i.resolvidas == null ? (
                          '—'
                        ) : (
                          <>
                            {numero(i.resolvidas)} resolvidas
                            {i.pendentes > 0 && (
                              <div style={{ fontSize: 12, color: '#9a5b06' }}>
                                {numero(i.pendentes)} pendentes
                              </div>
                            )}
                          </>
                        )}
                      </td>
                    </tr>

                    {aberto && (
                      <tr>
                        <td colSpan={7} style={{ background: 'var(--ff-surface-2)' }}>
                          <div className="p-3">
                            {i.mensagem && (
                              <div
                                className={`ff-aviso ff-aviso--${i.status === 'erro' ? 'atencao' : 'info'}`}
                                style={{ margin: '0 0 12px' }}
                              >
                                <i className="bi bi-info-circle" aria-hidden="true" />
                                <div>{i.mensagem}</div>
                              </div>
                            )}

                            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>
                              Conferências aritméticas
                            </div>
                            <Conferencias validacoes={i.validacoes} />

                            {i.id_arquivo && (
                              <div className="mt-3 d-flex gap-2">
                                <Link
                                  to={`/arquivos/${i.id_arquivo}`}
                                  className="ff-btn ff-btn--secundario ff-btn--pequeno"
                                >
                                  Abrir o arquivo
                                </Link>
                                {i.status === 'concluido' && (
                                  <Link
                                    to={`/protocolos?arquivo=${i.id_arquivo}`}
                                    className="ff-btn ff-btn--secundario ff-btn--pequeno"
                                  >
                                    Ver o que ele manda baixar
                                  </Link>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </Tabela>
        </Painel>
      </div>
    </>
  );
}
